import React, { useState } from 'react';
import {
  Table,
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Modal,
  CustomInput,
} from 'reactstrap';
import { TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ModalConsumer from '../../utility/Modal/ModalConsumer';
import { useAuthStore } from '../../../store/auth.store';
import { useToast } from '../../../contexts/ToastContext';

const useStyles = makeStyles((theme) => ({
  memberSearchInput: {
    marginBottom: '0 !important',
    '& input': {
      color: 'white',
      backgroundColor: 'transparent',
      minHeight: '32px',
      fontFamily: 'Poppins',
      padding: '4px 8px',
      fontSize: 'var(--menu-font-size)'
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'transparent',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.1) !important',
        top: 0,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.2) !important',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.2) !important',
      },
    },
    '& input::placeholder': {
      color: 'rgba(255,255,255,0.5)',
      opacity: 1,
      fontFamily: 'Poppins',
    }
  }
}));

// Sliding toggle pill — visually identical to the one on the Account page.
const ToggleSwitch = ({ checked, onChange, ariaLabel }) => (
  <button
    type="button"
    onClick={onChange}
    aria-label={ariaLabel}
    aria-pressed={checked}
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '36px',
      minWidth: '36px',
      height: '20px',
      borderRadius: '10px',
      border: 'none',
      backgroundColor: checked ? 'var(--accent-1-color)' : 'rgba(255, 255, 255, 0.2)',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      position: 'relative',
      padding: 0,
    }}
  >
    <div
      style={{
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        position: 'absolute',
        left: checked ? '18px' : '2px',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }}
    />
  </button>
);

// Per-member notifications editor opened from the Members table. Renders the
// same 7×2 toggle grid (in-app + email) as the user's own Account page,
// scoped to the selected member's membership doc.
const MemberNotificationsModal = ({ isOpen, onRequestClose, firebase, orgId, member }) => {
  const [open, setOpen] = React.useState(isOpen);
  const [prefs, setPrefs] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const { showSuccess, showError } = useToast();

  // Lazy-load shared notification definitions (TS module imported via dynamic import)
  const [types, setTypes] = React.useState([]);
  const [labels, setLabels] = React.useState({});

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const mod = await import('../../../services/notificationPreferences');
      if (cancelled) return;
      setTypes(mod.NOTIFICATION_TYPES_ORDER);
      setLabels(mod.NOTIFICATION_LABELS);
      try {
        const loaded = await firebase.getMemberNotificationPreferences(orgId, member.id);
        if (cancelled) return;
        setPrefs(loaded);
      } catch (err) {
        console.error('Failed to load member preferences:', err);
        setPrefs(mod.defaultPreferences());
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [firebase, orgId, member.id]);

  const toggle = () => setOpen(false);
  const handleClosed = () => onRequestClose();

  const togglePref = (channel, type) => {
    setPrefs((prev) => ({
      ...prev,
      [channel]: { ...prev[channel], [type]: !prev[channel][type] },
    }));
  };

  const save = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      await firebase.updateMemberNotificationPreferences(orgId, member.id, prefs);
      showSuccess(`Notification preferences saved for ${member.username || member.email}`);
      setOpen(false);
    } catch (err) {
      console.error('Failed to save member preferences:', err);
      showError(`Failed to save: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={open} toggle={toggle} onClosed={handleClosed} size="lg" contentClassName="delete-confirm-modal">
      <ModalHeader toggle={toggle}>
        Notifications — {member.username || member.email}
      </ModalHeader>
      <ModalBody>
        {loading || !prefs ? (
          <p className="text-muted mb-0">Loading…</p>
        ) : (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 90px 90px',
                gap: '0.5rem 1rem',
                alignItems: 'center',
                paddingBottom: '0.5rem',
                borderBottom: '1px solid rgba(255,255,255,0.08)',
                marginBottom: '0.25rem',
              }}
            >
              <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notification</span>
              <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>In-app</span>
              <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'center' }}>Email</span>
            </div>
            {types.map((type) => (
              <div
                key={type}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 90px 90px',
                  gap: '0.5rem 1rem',
                  alignItems: 'center',
                  padding: '0.65rem 0',
                  borderBottom: '1px solid rgba(255,255,255,0.04)',
                }}
              >
                <span style={{ color: '#fff', fontSize: '0.9rem' }}>{labels[type]}</span>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <ToggleSwitch
                    checked={prefs.inApp[type]}
                    onChange={() => togglePref('inApp', type)}
                    ariaLabel={`In-app ${labels[type]}`}
                  />
                </div>
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <ToggleSwitch
                    checked={prefs.email[type]}
                    onChange={() => togglePref('email', type)}
                    ariaLabel={`Email ${labels[type]}`}
                  />
                </div>
              </div>
            ))}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Button className="ml-auto btn-info-custom" onClick={save} disabled={saving || loading} style={{ position: 'relative', zIndex: 2 }}>
          <span style={{ position: 'relative', zIndex: 3 }}>{saving ? 'Saving…' : 'Save'}</span>
        </Button>
        <Button className="ml-2 btn-primary-custom" onClick={toggle} style={{ position: 'relative', zIndex: 2 }}>
          <span style={{ position: 'relative', zIndex: 3 }}>Cancel</span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const ConfirmModal = (props) => {
  const [modal, setModal] = useState(props.isOpen);
  const toggle = () => {
    setModal(!modal);
  };
  const remove = () => {
    props.onRequestClose();
  };
  const callback = () => {
    props.callback();
    toggle();
  };
  return (
    <div>
      <Modal isOpen={modal} toggle={toggle} onClosed={remove} contentClassName="delete-confirm-modal">
        <ModalHeader toggle={toggle}>{props.title}</ModalHeader>
        <ModalBody>{props.body}</ModalBody>
        <ModalFooter>
          <Button
            className="ml-auto btn-warning-custom"
            onClick={callback}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <span style={{ position: 'relative', zIndex: 3 }}>Continue</span>
          </Button>
          <Button
            className="ml-2 btn-primary-custom"
            onClick={toggle}
            style={{ position: 'relative', zIndex: 2 }}
          >
            <span style={{ position: 'relative', zIndex: 3 }}>Cancel</span>
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

const TableHeader = ({ label, sortKey, orderBy, onSort }) => {
  const isActive = orderBy.key === sortKey;
  const sortIcon = isActive ? (orderBy.direction === 'asc' ? '↓' : '↑') : '↕';

  return (
    <th>
      <h4 onClick={() => onSort(sortKey)} className="mb-0">
        {label} {sortIcon}
      </h4>
    </th>
  );
};

const MembersList = ({ members, firebase, orgId, onRefresh }) => {
  const classes = useStyles();
  const [orderBy, setOrderBy] = useState({ key: 'email', direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const auth = useAuthStore((state) => state.auth);
  const { showSuccess, showError } = useToast();

  const handleSort = (key) => {
    setOrderBy(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  const filteredMembers = [...members].filter(member => {
    if (!searchTerm) return true;
    return (
      member.email?.toLowerCase().includes(searchTerm) ||
      member.username?.toLowerCase().includes(searchTerm) ||
      member.role?.toLowerCase().includes(searchTerm)
    );
  });

  const sortedMembers = filteredMembers.sort((a, b) => {
    const aValue = a[orderBy.key] || '';
    const bValue = b[orderBy.key] || '';

    return orderBy.direction === 'asc'
      ? aValue.toString().localeCompare(bValue.toString())
      : bValue.toString().localeCompare(aValue.toString());
  });

  const removeMember = async (memberId, memberName) => {
    // Don't allow removing yourself
    if (memberId === auth?.uid) {
      showError('You cannot remove yourself from the organization.');
      return;
    }

    try {
      await firebase.removeMember(orgId, memberId);
      showSuccess(`${memberName} has been removed from the organization`);
      onRefresh();
    } catch (err) {
      console.error('Error removing member:', err);
      showError(`Error removing member: ${err.message}`);
    }
  };

  const updateMemberRole = async (memberId, newRole, memberName) => {
    try {
      await firebase.updateMemberRole(orgId, memberId, newRole);
      showSuccess(`${memberName}'s role updated to ${newRole}`);
      onRefresh();
    } catch (err) {
      console.error('Error updating member role:', err);
      showError(`Error updating role: ${err.message}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div>
      <div style={{ marginBottom: '1rem' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search members by email, username, or role..."
          value={searchTerm}
          onChange={handleSearch}
          className={classes.memberSearchInput}
        />
      </div>
      <div className="table-scroll-container" style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto' }}>
        <Table className="table-tracks" responsive>
          <thead>
            <tr>
              <TableHeader
                label="Email"
                sortKey="email"
                orderBy={orderBy}
                onSort={handleSort}
              />
              <TableHeader
                label="User Name"
                sortKey="username"
                orderBy={orderBy}
                onSort={handleSort}
              />
              <TableHeader
                label="Role"
                sortKey="role"
                orderBy={orderBy}
                onSort={handleSort}
              />
              <TableHeader
                label="Joined"
                sortKey="joinedAt"
                orderBy={orderBy}
                onSort={handleSort}
              />
              <th><h4 className="mb-0">Notifications</h4></th>
              <th>&nbsp;</th>
            </tr>
          </thead>
          <tbody>
            {sortedMembers.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center text-muted py-4">
                  No members found
                </td>
              </tr>
            ) : (
              sortedMembers.map((member, idx) => (
                <tr key={member.id} className={idx % 2 === 0 ? 'table-alt-row' : ''}>
                  <td>{member.email || '-'}</td>
                  <td>{member.username || '-'}</td>
                  <td>
                    {member.isOwner ? (
                      <span
                        style={{
                          color: '#ffffff',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family-primary)',
                          padding: '4px 8px',
                        }}
                      >
                        Owner
                      </span>
                    ) : (
                      <CustomInput
                        type="select"
                        id={`role-select-${member.id}`}
                        value={member.role || 'USER'}
                        onChange={(e) => updateMemberRole(member.id, e.target.value, member.username || member.email)}
                        disabled={member.id === auth?.uid}
                        style={{
                          backgroundColor: 'transparent',
                          color: '#ffffff',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: 'var(--border-radius-sm)',
                          padding: '4px 24px 4px 8px',
                          cursor: member.id === auth?.uid ? 'not-allowed' : 'pointer',
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='4' height='5' viewBox='0 0 4 5'%3e%3cpath fill='%23ffffff' d='M2 0l2 2H0zm0 5L0 3h4z'/%3e%3c/svg%3e")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 0.5rem center',
                          backgroundSize: '12px 10px',
                          appearance: 'none',
                          fontSize: '14px',
                          fontFamily: 'var(--font-family-primary)',
                          transition: 'all var(--transition-fast)',
                          outline: 'none',
                          opacity: member.id === auth?.uid ? 0.6 : 1,
                        }}
                        className="role-select-custom"
                      >
                        <option value="ADMIN">Admin</option>
                        <option value="OPERATOR">Operator</option>
                        <option value="USER">User</option>
                      </CustomInput>
                    )}
                  </td>
                  <td>{formatDate(member.joinedAt)}</td>
                  <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    <ModalConsumer>
                      {({ showModal }) => (
                        <Button
                          className="btn-primary-custom btn-round"
                          size="sm"
                          style={{ display: 'inline-block' }}
                          title="Edit notification preferences"
                          onClick={() => {
                            showModal(MemberNotificationsModal, {
                              isOpen: true,
                              firebase,
                              orgId,
                              member,
                            });
                          }}
                        >
                          <span className="d-flex flex-row align-items-center my-auto">
                            <i className="tim-icons icon-bell-55"></i>
                          </span>
                        </Button>
                      )}
                    </ModalConsumer>
                  </td>
                  <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                    {member.isOwner ? (
                      <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px' }}>—</span>
                    ) : (
                      <ModalConsumer>
                        {({ showModal }) => (
                          <Button
                            className="btn-primary-custom btn-round"
                            size="sm"
                            style={{ display: 'inline-block' }}
                            disabled={member.id === auth?.uid}
                            onClick={() => {
                              showModal(ConfirmModal, {
                                isOpen: true,
                                title: 'Remove Member',
                                body: `Are you sure you want to remove ${member.username || member.email} from this organization?`,
                                callback: () => {
                                  removeMember(member.id, member.username || member.email);
                                },
                              });
                            }}
                          >
                            <span className="d-flex flex-row align-items-center my-auto">
                              <i className="tim-icons icon-trash-simple"></i>{' '}
                              Remove
                            </span>
                          </Button>
                        )}
                      </ModalConsumer>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default MembersList;
